import { isValidPhoneNumber } from "@ews-admin/global-design-system";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ProvidedServiceEnum, ResidencyEnum } from "../types/doctor.definition";
import { isCompletePhoneNumber } from "../utils/phone.utils";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../types/user.definition";

// Helper function to validate age
const validateAge = (dateString: string, minAge: number): boolean => {
  const birthDate = new Date(dateString);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  const actualAge =
    monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

  return actualAge >= minAge;
};

// Helper function to validate career start year
const validateCareerStartYear = (
  careerStartYear: string,
  dateOfBirth: string
): boolean => {
  if (!dateOfBirth || !careerStartYear) return true;

  const birthDate = new Date(dateOfBirth);
  const careerDate = new Date(careerStartYear);
  const careerYear = careerDate.getFullYear();
  const birthYear = birthDate.getFullYear();

  // Career should start at least 18 years after birth
  return careerYear - birthYear >= 18;
};

// Specialty schema (simplified)
const specialtySchema = z.object({
  id: z.number().optional(),
  code: z.string(),
  label: z.string(),
});

export const doctorFormSchema = (t: TFunction, isEditMode: boolean = false) =>
  z
    .object({
      email: z
        .string()
        .nonempty(t("common.validation.required"))
        .email(t("common.validation.invalidEmail")),
      password: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return val.length >= 8;
            }
            return true;
          },
          { message: t("common.validation.passwordTooShort") }
        )
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(val);
            }
            return true;
          },
          { message: t("common.validation.passwordComplexity") }
        ),
      confirmPassword: z.string().optional(),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().nonempty(t("common.validation.required")).refine(
        (val) => (isEditMode ? isValidPhoneNumber(val) : isCompletePhoneNumber(val)),
        { message: t("common.validation.invalidPhoneNumber") }
      ),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => validateAge(date, 18), {
          message: t("common.validation.minimumAge", { age: 18 }),
        })
        .refine((date) => validateAge(date, 0) && !validateAge(date, 100), {
          message: t("common.validation.invalidDateOfBirth"),
        }),
      careerStartYear: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const careerDate = new Date(date);
          return !isNaN(careerDate.getTime()) && careerDate <= new Date();
        }, t("common.validation.careerStartInPast")),
      refNumber: isEditMode
        ? z.string().optional()
        : z.string().nonempty(t("common.validation.required")),
      roleName: z.literal(RoleEnum.DOCTOR),
      specialties: z.array(specialtySchema).optional(),
      providedServices: z
        .array(
          z.object({
            code: z.nativeEnum(ProvidedServiceEnum),
            price: z.number(),
          })
        )
        .optional(),
      biography: z
        .string()
        .min(20, { message: t("common.validation.minLength", { length: 20 }) })
        .max(500, { message: t("common.validation.maxLength", { length: 500 }) })
        .optional()
        .or(z.literal("")),
      hospitalName: z.string().optional().or(z.literal("")),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]))
        .min(1, t("common.validation.selectAtLeastOne")),
      activated: z.boolean().optional(),
      healthcareFacilityIds: z.array(z.number()).optional(),
      doctorResidency: z.nativeEnum(ResidencyEnum).optional(),
      doctorSpokenLanguages: z.array(z.string()).optional(),
    })
    .refine(
      (data) => {
        // Only validate password match if password is provided
        if (data.password && data.password.trim() !== "") {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: t("common.validation.passwordMismatch"),
        path: ["confirmPassword"],
      }
    )
    .refine(
      (data) => {
        if (data.careerStartYear && data.dateOfBirth) {
          return validateCareerStartYear(
            data.careerStartYear,
            data.dateOfBirth
          );
        }
        return true;
      },
      {
        message: t("common.validation.careerStartAfterBirth"),
        path: ["careerStartYear"],
      }
    );

export const useDoctorFormSchema = () => {
  const { t } = useTranslation();
  return doctorFormSchema(t);
};

export const editDoctorFormSchema = (t: TFunction) =>
  z
    .object({
      email: z
        .string()
        .nonempty(t("common.validation.required"))
        .email(t("common.validation.invalidEmail")),
      password: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return val.length >= 8;
            }
            return true;
          },
          { message: t("common.validation.passwordTooShort") }
        )
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(val);
            }
            return true;
          },
          { message: t("common.validation.passwordComplexity") }
        ),
      confirmPassword: z.string().optional(),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().nonempty(t("common.validation.required")).refine(
        (val) => isValidPhoneNumber(val),
        { message: t("common.validation.invalidPhoneNumber") }
      ),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => validateAge(date, 18), {
          message: t("common.validation.minimumAge", { age: 18 }),
        }),
      careerStartYear: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const careerDate = new Date(date);
          return !isNaN(careerDate.getTime()) && careerDate <= new Date();
        }, t("common.validation.careerStartInPast")),
      refNumber: z.string().optional(),
      roleName: z.literal(RoleEnum.DOCTOR),
      specialties: z.array(specialtySchema).optional(),
      providedServices: z
        .array(
          z.object({
            code: z.nativeEnum(ProvidedServiceEnum),
            price: z.number(),
          })
        )
        .optional(),
      biography: z.string().optional().or(z.literal("")),
      hospitalName: z.string().optional().or(z.literal("")),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]))
        .min(1, t("common.validation.selectAtLeastOne")),
      activated: z.boolean().optional(),
      healthcareFacilityIds: z.array(z.number()).optional(),
      doctorResidency: z.nativeEnum(ResidencyEnum).optional(),
      doctorSpokenLanguages: z.array(z.string()).optional(),
    })
    .refine(
      (data) => {
        // Only validate password match if password is provided
        if (data.password && data.password.trim() !== "") {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: t("common.validation.passwordMismatch"),
        path: ["confirmPassword"],
      }
    )
    .refine(
      (data) => {
        if (data.careerStartYear && data.dateOfBirth) {
          return validateCareerStartYear(
            data.careerStartYear,
            data.dateOfBirth
          );
        }
        return true;
      },
      {
        message: t("common.validation.careerStartAfterBirth"),
        path: ["careerStartYear"],
      }
    );

export const useEditDoctorFormSchema = () => {
  const { t } = useTranslation();
  return editDoctorFormSchema(t);
};

export type DoctorFormSchema = z.infer<ReturnType<typeof useDoctorFormSchema>>;

export type EditDoctorFormSchema = z.infer<
  ReturnType<typeof useEditDoctorFormSchema>
>;
